import React, { useState, useEffect } from 'react';
import { MarkerManager } from '../../lib/map/managers/MarkerManager';

export interface MapMarkerProps {
  id: string;
  title?: string;
  color?: string;
  size?: number;
  selected?: boolean;
  draggable?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  markerManager?: MarkerManager;
}

export const MapMarker: React.FC<MapMarkerProps> = ({
  id,
  color = '#ff6b6b',
  size = 20,
  selected = false,
  draggable = false,
  title,
  onClick,
  markerManager,
}) => {
  const [isSelected, setIsSelected] = useState(selected);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!markerManager) return;

    const handleSelect = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { markerId } = customEvent.detail;
      setIsSelected(markerId === null ? false : markerId === id);
    };

    const handleHover = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { markerId } = customEvent.detail;
      setIsHovered(markerId === id);
    };

    markerManager.addEventListener('marker:select', handleSelect);
    markerManager.addEventListener('marker:hover', handleHover);

    return () => {
      markerManager.removeEventListener('marker:select', handleSelect);
      markerManager.removeEventListener('marker:hover', handleHover);
    };
  }, [id, markerManager]);

  const finalSelected = isSelected || isHovered;
  const markerSize = finalSelected ? size * 1.65 : size;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      onClick={handleClick}
      style={{
        width: markerSize,
        height: markerSize,
        borderRadius: '50%',
        backgroundColor: color,
        border: '2px solid white',
        cursor: draggable ? 'move' : 'pointer',
        boxShadow: draggable ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)',
        transition: 'all 0.2s ease',
        borderColor: finalSelected ? '#4ecdc4' : 'white',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {title && (
        <span
          style={{
            color: 'white',
            fontSize: Math.max(8, markerSize * 0.4),
            fontWeight: 'bold',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            lineHeight: 1,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 10,
            position: 'relative',
          }}>
          {title}
        </span>
      )}
      {draggable && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.3)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}
    </button>
  );
};

export default MapMarker;
